<script setup lang="ts">
    import ShareIcons from '@shared/components/ShareIcons.vue';
    import TagsList from '@shared/components/Tags/TagsList.vue';
    import { formatDateTime } from '@shared/utils/formatDateTime';
    import { formatNumericText } from '@shared/utils/formatNumericText';
    import { copyToClipboard } from '@views/Web/copyToClipboard';
    import { type SurveyStatisticsSummary, type SurveyStatistics } from '../../SurveysTypes';

    const props = defineProps<{
        summary: SurveyStatisticsSummary;
        stats: SurveyStatistics | null;
        hasAdvancedStatistics: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'show-responses'): void;
        (e: 'show-respondents'): void;
    }>();

    const showEmbedCode = defineModel<boolean>('showEmbedCode', { default: false });

    const iframeCode = (url: string) => `<iframe width="100%" height="600" src="${url}" frameborder="0"></iframe>`;
</script>

<template>
    <div class="list-header">
        <h1>
            <span>{{ summary.Title.length !== 0 ? summary.Title : $Resources.NoTitlePlaceholder }}</span>
            <small class="m-sm-l" v-if="!summary.End">{{ $Resources.SurveyStatusActiveLabel }}</small>
            <small class="m-sm-l" v-if="summary.End && new Date(summary.End) < new Date()">{{ $Resources.SurveyEndedLabel }}</small>
        </h1>

        <p v-if="summary.SurveyName">{{ summary.SurveyName }}</p>

        <template v-if="summary.IsActive">
            <ShareIcons :id="summary.SurveyId" :label="$Resources.ShareLabel" type="Surveys"></ShareIcons>

            <p>
                <span class="fw-600 m-xs-r">{{ $Resources.SurveyUrlLabel }}:</span>
                <a :href="summary.Url" target="_blank" rel="noopener">{{ summary.Url }}</a>
                <button class="btn btn-subtle btn-xs hidden-print m-xxs-l" @click="copyToClipboard(summary.Url)">{{ $Resources.CopyToClipBoardBtn }}</button>
                <button class="btn btn-subtle btn-xs hidden-print m-xxs-l" type="button" @click="showEmbedCode = !showEmbedCode">Iframe</button>
            </p>

            <div v-if="showEmbedCode" class="form-group">
                <h3 class="h5">{{ $Resources.PublishedSurveyEmbedAtWebpageLabel }}:</h3>
                <pre class="text-wrap">{{ iframeCode(summary.Url) }}</pre>
                <button type="button" class="btn btn-default btn-xs" @click="copyToClipboard(iframeCode(summary.Url))">
                    {{ $Resources.CopyToClipBoardBtn }}
                </button>
            </div>
        </template>

        <ul
            v-if="!stats"
            class="list-unstyled indicator-list d-flex flex-justified flex-wrap gap-lg m-sm-t m-lg-b"
            style="--indicator-font-size: var(--font-size-base)">
            <li>
                <span class="indicator-label">{{ $Resources.SurveyResponseCountLabel }}</span>
                <span class="indicator-status" :class="{ success: summary.TotalResponseCount > 0 }"></span>
                {{ formatNumericText(summary.TotalResponseCount, { largeNumbers: true }) }}
                <small v-if="summary.ResponseLimit > 0">
                    {{ $Resources.ResponseLimitLabel.replace('{ResponseLimit}', String(summary.ResponseLimit)) }}
                </small>
            </li>
            <li>
                <span class="indicator-label">{{ $Resources.SurveyRespondentCountLabel }}</span>
                <span class="indicator-status" :class="{ success: summary.TotalResponseCount > 0 }"></span>
                {{ formatNumericText(summary.TotalRespondentCount, { largeNumbers: true }) }}
            </li>
        </ul>

        <ul
            v-else
            class="list-unstyled indicator-list d-flex flex-justified flex-wrap gap-lg m-sm-t m-lg-b"
            style="--indicator-font-size: var(--font-size-base)">
            <li>
                <span class="indicator-label">{{ $Resources.SurveyResponseCountLabel }}</span>
                <span class="indicator-status" :class="{ success: stats.ResponseCount > 0 }"></span>
                {{ formatNumericText(stats.ResponseCount, { largeNumbers: true }) }}
                <small v-if="stats.ResponseCount !== stats.TotalResponseCount">
                    {{ formatNumericText(stats.TotalResponseCount, { largeNumbers: true, prefix: '(', suffix: ')' }) }}
                </small>
                <small v-if="stats.ResponseLimit > 0 && !stats.EventId">
                    {{ $Resources.ResponseLimitLabel.replace('{ResponseLimit}', String(stats.ResponseLimit)) }}
                </small>
                <button v-if="stats.ResponseCount > 0" type="button" class="btn btn-primary btn-sm m-xs-l hidden-print" @click="emit('show-responses')">
                    {{ $Resources.SurveyShowResponsesBtn }}
                </button>
            </li>
            <li>
                <span class="indicator-label">{{ $Resources.SurveyRespondentCountLabel }}</span>
                <span class="indicator-status" :class="{ success: stats.ResponseCount > 0 }"></span>
                {{ formatNumericText(stats.RespondentCount, { largeNumbers: true }) }}
                <small v-if="stats.RespondentCount !== stats.TotalRespondentCount">
                    {{ formatNumericText(stats.TotalRespondentCount, { largeNumbers: true, prefix: '(', suffix: ')' }) }}
                </small>
                <button v-if="stats.ResponseCount > 0" type="button" class="btn btn-primary btn-sm m-xs-l hidden-print" @click="emit('show-respondents')">
                    {{ $Resources.SurveyShowRespondentsBtn }}
                </button>
            </li>
            <li>
                <span class="indicator-label">{{ $Resources.SurveyStatisticsSurveyOpenedCountLabel }}</span>
                <span class="indicator-status" :class="{ success: stats.SurveyOpenedCount > 0 }"></span>
                {{ formatNumericText(stats.SurveyOpenedCount, { largeNumbers: true }) }}
                <small v-if="stats.SurveyOpenedCount !== stats.TotalSurveyOpenedCount">
                    {{ formatNumericText(stats.TotalSurveyOpenedCount, { largeNumbers: true, prefix: '(', suffix: ')' }) }}
                </small>
            </li>
            <template v-if="hasAdvancedStatistics">
                <li>
                    <span class="indicator-label">{{ $Resources.SurveyAverageDuration }}</span>
                    <span class="indicator-status" :class="{ success: (stats.AverageResponseTime ?? 0) > 0 }"></span>
                    {{ formatNumericText(stats.AverageResponseTime, { largeNumbers: true, suffix: ' s' }) }}
                </li>
                <li>
                    <span class="indicator-label">{{ $Resources.SurveyFinished }}</span>
                    <span class="indicator-status" :class="{ success: stats.SurveyOpenedCount > 0 }"></span>
                    {{ formatNumericText(stats.SurveyOpenedCount > 0 ? (stats.TotalResponseCount / stats.SurveyOpenedCount) * 100 : 0, { suffix: '%' }) }}
                </li>
            </template>
        </ul>

        <TagsList v-if="summary.Tags.length > 0" :tags="summary.Tags" readonly showHeading></TagsList>

        <p class="meta">
            <template v-if="summary.Start">
                <span class="glyphicon glyphicon-time"></span>
                {{ $Resources.SurveyStartedLabel }}:
                <span>{{ formatDateTime(summary.Start) }}</span>
            </template>
            <template v-if="summary.End">
                <span class="glyphicon glyphicon-time"></span>
                {{ $Resources.SurveyEndedLabel }}:
                <span>{{ formatDateTime(summary.End) }}</span>
            </template>

            <i class="fa fa-pencil" aria-hidden="true"></i>
            {{ $Resources.SurveyEditedWhenLabel }}
            <span>{{ formatDateTime(summary.LastModified) }}</span>
            <span class="glyphicon glyphicon-user"></span>
            {{ $Resources.SurveyEditedByLabel }}
            <span>{{ summary.LastModifiedBy.Name }}</span>
        </p>
    </div>
</template>
